import { type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import { currencyWord, formatNotificationAmount, renderNotificationTemplate } from '@mlm/shared';
import { adjustSlot, getTenantFeatures, type AdjustTarget } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

type AdjustKind = 'wallet' | 'points';

// Wallet currencies a comp plan can pay into — adjusting these simulates income.
const WALLET_TARGETS: Array<{ value: AdjustTarget; label: string }> = [
  { value: 'cash', label: 'Cash wallet' },
  { value: 'gc', label: 'Gift Cert (GC)' },
  { value: 'coin', label: 'Coins' },
];

// Point balances. PV is a purchase simulator (add-only, cascades GPV upline);
// Vortex tokens are granted/spent directly.
const POINT_TARGETS: Array<{ value: AdjustTarget; label: string }> = [
  { value: 'pv', label: 'PV (simulate purchase)' },
  { value: 'token', label: 'Vortex token' },
];

// Reward types a wallet credit can be attributed to (simulate income), plus the
// generic admin adjustment. Mirrors the legacy plan-type picker.
const ADJUST_SOURCE_PLANS: Array<{ value: string; label: string }> = [
  { value: 'admin_adjust', label: 'Admin adjustment' },
  { value: 'direct', label: 'Direct referral' },
  { value: 'indirect', label: 'Indirect' },
  { value: 'binary', label: 'Binary' },
  { value: 'unilevel', label: 'Unilevel' },
  { value: 'stairstep', label: 'Stairstep' },
  { value: 'mentor_bonus', label: 'Mentor bonus' },
  { value: 'infinity_bonus', label: 'Infinity bonus on activation' },
  { value: 'sign_up_bonus', label: 'Sign-up bonus' },
  { value: 'australian_pass_up', label: 'Australian pass-up' },
  { value: 'monthly_direct_bonus', label: 'Monthly direct bonus' },
  { value: 'unilevel_consecutive_bonus', label: 'Unilevel consecutive bonus' },
];

/**
 * Manual wallet OR points adjustment for one slot — separate dialogs by `kind`:
 *   - kind="wallet": add/deduct cash/GC/coins, attributed to a reward type (or
 *     admin_adjust). Simulates income.
 *   - kind="points": Vortex tokens (add/deduct) or PV. PV is a SIMULATOR —
 *     adding it behaves like the member buying a product, so it credits this
 *     slot's Personal PV and cascades Group PV up the sponsor line. PV is
 *     therefore add-only.
 *
 * The notification is prefilled from the tenant earnings template (the message
 * members normally see) and stays editable; the `{trigger_user}` placeholder is
 * filled with the acting admin's name (not a dash).
 */
export function AdjustDialog({
  kind,
  userId,
  slotId,
  slotCode,
  compact = false,
}: {
  kind: AdjustKind;
  userId: string;
  slotId: string;
  slotCode: string;
  /** Short button label (for tight table rows). */
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const adminName = useAuthStore((s) => s.user?.fullName ?? s.user?.email ?? 'Admin');

  const targets = kind === 'wallet' ? WALLET_TARGETS : POINT_TARGETS;

  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState<'C' | 'D'>('C');
  const [target, setTarget] = useState<AdjustTarget>(targets[0]!.value);
  const [amount, setAmount] = useState('');
  const [sourcePlan, setSourcePlan] = useState('admin_adjust');
  const [note, setNote] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [touchedMsg, setTouchedMsg] = useState(false);

  const isPv = target === 'pv';
  const coinLabel = featuresQ.data?.coin_label ?? 'Coins';
  const showRewardType = kind === 'wallet';

  // Default (template-rendered) notification text for the current form values.
  function defaultMessage(eff: { direction: 'C' | 'D'; target: AdjustTarget; amount: string; sourcePlan: string; note: string }): {
    title: string;
    body: string;
  } {
    const pv = eff.target === 'pv';
    const tpl = featuresQ.data?.notification_templates?.earnings;
    const currency = pv ? 'PV' : currencyWord(eff.target as 'cash' | 'gc' | 'token' | 'coin', coinLabel);
    const amt = pv
      ? eff.amount || '0'
      : formatNotificationAmount(eff.amount || '0', eff.target as 'cash' | 'gc' | 'token' | 'coin');
    const vars = {
      amount: amt,
      currency,
      source_plan: pv
        ? 'Purchase (PV)'
        : ADJUST_SOURCE_PLANS.find((p) => p.value === eff.sourcePlan)?.label ?? eff.sourcePlan,
      kind: eff.direction === 'C' ? 'credit' : 'deduction',
      trigger_user: adminName,
    };
    if (tpl?.enabled && tpl.title && tpl.body) {
      return {
        title: renderNotificationTemplate(tpl.title, vars),
        body: renderNotificationTemplate(tpl.body, vars),
      };
    }
    const verb = eff.direction === 'C' ? 'credited to' : 'deducted from';
    return {
      title: eff.direction === 'C' ? 'Account credit' : 'Account adjustment',
      body: `${amt} ${currency} was ${verb} your slot ${slotCode}${eff.note ? ` — ${eff.note}` : ''}.`,
    };
  }

  function applyDefault(eff: { direction: 'C' | 'D'; target: AdjustTarget; amount: string; sourcePlan: string; note: string }) {
    const d = defaultMessage(eff);
    setTitle(d.title);
    setBody(d.body);
  }

  function openDialog() {
    const t = targets[0]!.value;
    setDirection('C');
    setTarget(t);
    setAmount('');
    setSourcePlan('admin_adjust');
    setNote('');
    setSendNotification(true);
    setTouchedMsg(false);
    applyDefault({ direction: 'C', target: t, amount: '', sourcePlan: 'admin_adjust', note: '' });
    setOpen(true);
  }

  // PV is add-only; force credit when PV is selected.
  function selectTarget(v: AdjustTarget) {
    setTarget(v);
    const dir = v === 'pv' ? 'C' : direction;
    if (v === 'pv') setDirection('C');
    if (!touchedMsg) applyDefault({ direction: dir, target: v, amount, sourcePlan, note });
  }

  const mutation = useMutation({
    mutationFn: () =>
      adjustSlot(slotId, {
        direction,
        target,
        amount,
        sourcePlan,
        note,
        sendNotification,
        notificationTitle: sendNotification ? title : undefined,
        notificationBody: sendNotification ? body : undefined,
      }),
    meta: { successMessage: 'Adjustment saved.' },
    onSuccess: () => {
      // Refresh whichever host page is showing balances: member detail (user-*
      // scoped to this member), plus the global Slots list and Slot detail page
      // (any 'admin-slot…' key).
      qc.invalidateQueries({ queryKey: ['user-wallet', userId] });
      qc.invalidateQueries({ queryKey: ['user-slot-wallets', userId] });
      qc.invalidateQueries({ queryKey: ['user-ledger', userId] });
      qc.invalidateQueries({ queryKey: ['user-slots', userId] });
      qc.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('admin-slot'),
      });
      setOpen(false);
    },
  });

  const amountValid = /^\d+(\.\d{1,4})?$/.test(amount) && Number(amount) > 0;
  const errMsg =
    (mutation.error as { response?: { data?: { error?: string; detail?: string } } } | null)
      ?.response?.data;
  const fullLabel = kind === 'wallet' ? 'Adjust Wallet' : 'Adjust Points';
  const btnLabel = compact ? (kind === 'wallet' ? 'Wallet' : 'Points') : fullLabel;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? openDialog() : setOpen(false))}>
      <Button size="sm" variant="outline" onClick={openDialog}>
        {btnLabel}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {fullLabel} · <span className="font-mono text-sm">{slotCode}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Action">
              <Select
                value={direction}
                onValueChange={(v) => {
                  setDirection(v as 'C' | 'D');
                  if (!touchedMsg) applyDefault({ direction: v as 'C' | 'D', target, amount, sourcePlan, note });
                }}
                disabled={isPv}
              >
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="C">Add (credit)</SelectItem>
                  <SelectItem value="D">Deduct (debit)</SelectItem>
                </SelectContent>
              </Select>
            </Labeled>
            <Labeled label="Target">
              <Select value={target} onValueChange={(v) => selectTarget(v as AdjustTarget)}>
                <SelectTrigger className="h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.value === 'coin' ? coinLabel : t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Labeled>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Labeled label={isPv ? 'PV amount' : 'Amount'}>
              <Input
                className="h-8"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (!touchedMsg) applyDefault({ direction, target, amount: e.target.value, sourcePlan, note });
                }}
                placeholder="0.00"
              />
            </Labeled>
            {showRewardType && (
              <Labeled label="Reward type">
                <Select
                  value={sourcePlan}
                  onValueChange={(v) => {
                    setSourcePlan(v);
                    if (!touchedMsg) applyDefault({ direction, target, amount, sourcePlan: v, note });
                  }}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADJUST_SOURCE_PLANS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Labeled>
            )}
          </div>

          {isPv && (
            <p className="text-[11px] text-muted-foreground">
              Simulates a self-purchase: adds Personal PV to this slot and cascades Group PV up the
              sponsor line. Add-only.
            </p>
          )}

          <Labeled label="Reason (audit + default message)">
            <Input
              className="h-8"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (!touchedMsg) applyDefault({ direction, target, amount, sourcePlan, note: e.target.value });
              }}
              placeholder="e.g. simulated direct bonus / balance correction"
            />
          </Labeled>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={sendNotification}
              onCheckedChange={(v) => setSendNotification(v === true)}
            />
            Send an in-app notification to the member
          </label>

          {sendNotification && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Notification</span>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    applyDefault({ direction, target, amount, sourcePlan, note });
                    setTouchedMsg(false);
                  }}
                >
                  Reset to default
                </button>
              </div>
              <Input
                className="h-8"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setTouchedMsg(true);
                }}
                placeholder="Title"
              />
              <Textarea
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setTouchedMsg(true);
                }}
                rows={3}
                placeholder="Message body"
              />
            </div>
          )}

          {errMsg && (
            <p className="text-sm text-destructive">
              {errMsg.error === 'insufficient_balance'
                ? `Insufficient balance — ${errMsg.detail ?? ''}`
                : (errMsg.error ?? 'Adjustment failed.')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button disabled={!amountValid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending
              ? 'Applying…'
              : direction === 'C'
                ? 'Add to slot'
                : 'Deduct from slot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {children}
    </div>
  );
}
